import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Loader2, Trash2, FileText, ShieldCheck, Pill, Stethoscope, Mic, Square, Sparkles } from "lucide-react";
import CidAutocomplete from "@/components/CidAutocomplete";

export default function ProntuarioTab({ patientId }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const [f, setF] = useState({
    date: new Date().toISOString().slice(0, 10),
    reason: "", subjective: "", objective: "", assessment: "", plan: "",
    dxCode: "", dxDesc: "", medName: "", medDose: "", medInstr: "",
  });

  const load = () => api.get(`/patients/${patientId}/consultations`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [patientId]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const startRec = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error("Gravação não suportada neste navegador/contexto (requer HTTPS e microfone).");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => uploadAudio(new Blob(chunksRef.current, { type: "audio/webm" }));
      recorderRef.current = mr;
      mr.start();
      setRecording(true);
      toast.info("Gravando… fale a consulta e clique em parar quando terminar.");
    } catch (e) {
      toast.error("Não foi possível aceder ao microfone. Verifique as permissões.");
    }
  };

  const stopRec = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      setRecording(false);
      setTranscribing(true);
    }
  };

  const uploadAudio = async (blob) => {
    try {
      const fd = new FormData();
      fd.append("audio", blob, "consulta.webm");
      const { data } = await api.post(`/patients/${patientId}/transcribe-consult`, fd);
      const s = data.soap || {};
      setTranscript(data.transcript || "");
      setF((prev) => ({
        ...prev,
        reason: s.reason || prev.reason,
        subjective: s.subjective || prev.subjective,
        objective: s.objective || prev.objective,
        assessment: s.assessment || prev.assessment,
        plan: s.plan || prev.plan,
        dxCode: s.suggested_cid || prev.dxCode,
      }));
      setOpen(true);
      toast.success("Transcrição pronta — revise e assine.");
    } catch (e) {
      toast.error("Falha ao transcrever o áudio. Tente novamente.");
    } finally { setTranscribing(false); }
  };

  const save = async () => {
    if (!f.assessment.trim() && !f.subjective.trim()) { toast.error("Preencha a avaliação/queixa"); return; }
    setSaving(true);
    try {
      const diagnoses = f.dxDesc ? [{ code: f.dxCode || null, description: f.dxDesc }] : [];
      const medications = f.medName ? [{ name: f.medName, dosage: f.medDose || null, instructions: f.medInstr || null }] : [];
      await api.post(`/patients/${patientId}/consultations`, {
        date: f.date, reason: f.reason, subjective: f.subjective, objective: f.objective,
        assessment: f.assessment, plan: f.plan, diagnoses, medications, sign: true,
      });
      toast.success("Consulta registrada e assinada");
      setOpen(false);
      setF({ ...f, reason: "", subjective: "", objective: "", assessment: "", plan: "", dxCode: "", dxDesc: "", medName: "", medDose: "", medInstr: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (cid) => {
    await api.delete(`/patients/${patientId}/consultations/${cid}`);
    load(); toast.success("Consulta removida");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-xl font-medium">Prontuário médico</h3>
        <div className="flex gap-2">
          {!recording ? (
            <Button variant="outline" onClick={startRec} disabled={transcribing} data-testid="record-consult-btn">
              {transcribing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mic className="w-4 h-4 mr-2 text-ai" />}
              {transcribing ? "A transcrever…" : "Gravar com IA"}
            </Button>
          ) : (
            <Button variant="destructive" onClick={stopRec} data-testid="stop-consult-btn" className="animate-pulse">
              <Square className="w-4 h-4 mr-2" /> Parar gravação
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-consultation-btn"><Plus className="w-4 h-4 mr-2" /> Nova consulta</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="consultation-dialog">
            <DialogHeader>
              <DialogTitle>Registrar consulta (SOAP)</DialogTitle>
              <DialogDescription>A nota é assinada digitalmente pelo médico responsável.</DialogDescription>
            </DialogHeader>
            {transcript && (
              <div className="rounded-md border border-ai/30 bg-ai/5 p-3 text-xs text-muted-foreground" data-testid="transcript-box">
                <p className="flex items-center gap-1 font-medium text-ai mb-1"><Sparkles className="w-3.5 h-3.5" /> Transcrição por IA (revise os campos abaixo)</p>
                <p className="max-h-24 overflow-y-auto whitespace-pre-wrap">{transcript}</p>
              </div>
            )}
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Data</Label>
                  <Input type="date" value={f.date} onChange={set("date")} data-testid="consultation-date" /></div>
                <div className="space-y-2"><Label>Motivo</Label>
                  <Input value={f.reason} onChange={set("reason")} data-testid="consultation-reason" /></div>
              </div>
              <div className="space-y-2"><Label>S — Subjetivo (queixa)</Label>
                <Textarea rows={2} value={f.subjective} onChange={set("subjective")} data-testid="consultation-subjective" /></div>
              <div className="space-y-2"><Label>O — Objetivo (exame físico)</Label>
                <Textarea rows={2} value={f.objective} onChange={set("objective")} data-testid="consultation-objective" /></div>
              <div className="space-y-2"><Label>A — Avaliação</Label>
                <Textarea rows={2} value={f.assessment} onChange={set("assessment")} data-testid="consultation-assessment" /></div>
              <div className="space-y-2"><Label>P — Plano / conduta</Label>
                <Textarea rows={2} value={f.plan} onChange={set("plan")} data-testid="consultation-plan" /></div>
              <div className="grid grid-cols-3 gap-3 border-t border-border pt-3">
                <div className="space-y-2 col-span-1"><Label>CID</Label>
                  <CidAutocomplete value={f.dxCode}
                    onSelect={(code, desc) => setF((p) => ({ ...p, dxCode: code, dxDesc: desc || p.dxDesc }))}
                    placeholder="Ex: I10" testid="consultation-cid" /></div>
                <div className="space-y-2 col-span-2"><Label>Diagnóstico</Label><Input value={f.dxDesc} onChange={set("dxDesc")} data-testid="consultation-dx" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label>Medicamento</Label><Input value={f.medName} onChange={set("medName")} data-testid="consultation-med" /></div>
                <div className="space-y-2"><Label>Dose</Label><Input value={f.medDose} onChange={set("medDose")} data-testid="consultation-dose" /></div>
                <div className="space-y-2"><Label>Instruções</Label><Input value={f.medInstr} onChange={set("medInstr")} data-testid="consultation-instr" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving} data-testid="save-consultation-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Assinar e salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground border-dashed">
          <FileText className="w-8 h-8 mx-auto mb-3 opacity-40" /> Nenhuma consulta registrada.
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((c) => (
            <Card key={c.id} className="p-6 border-border shadow-none" data-testid={`consultation-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-heading text-lg font-medium">{c.reason || "Consulta"}</h4>
                  <p className="text-sm text-muted-foreground">{c.date} · {c.doctor_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  {c.signature && (
                    <Badge variant="outline" className="bg-success/12 text-success border-success/25">
                      <ShieldCheck className="w-3 h-3 mr-1" /> Assinada
                    </Badge>
                  )}
                  <Button size="icon" variant="ghost" onClick={() => remove(c.id)} data-testid={`delete-consultation-${c.id}`}>
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                {c.subjective && <p><span className="font-semibold">S:</span> {c.subjective}</p>}
                {c.objective && <p><span className="font-semibold">O:</span> {c.objective}</p>}
                {c.assessment && <p><span className="font-semibold">A:</span> {c.assessment}</p>}
                {c.plan && <p><span className="font-semibold">P:</span> {c.plan}</p>}
              </div>
              {c.diagnoses?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {c.diagnoses.map((d, i) => (
                    <Badge key={i} variant="secondary"><Stethoscope className="w-3 h-3 mr-1" />{d.code ? `${d.code} · ` : ""}{d.description}</Badge>
                  ))}
                </div>
              )}
              {c.medications?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {c.medications.map((m, i) => (
                    <Badge key={i} variant="outline" className="border-ai/30 text-ai bg-ai/8">
                      <Pill className="w-3 h-3 mr-1" />{m.name}{m.dosage ? ` ${m.dosage}` : ""}
                    </Badge>
                  ))}
                </div>
              )}
              {c.signature && (
                <p className="text-xs text-muted-foreground mt-3 font-mono-data">
                  Assinado por {c.signature.signed_by} · hash {c.signature.hash}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
